import { useTranslation } from 'next-i18next';
import React, { VFC, useState, useEffect, useContext } from 'react';

import { masterSearch } from '@/apis/aniftyBackend/marketplace/masterSearch';
import MasterSearchModal from '@/containers/MarketplacePage/FilterSideBar/MasterSearchBar/MasterSearchModal';
import MarketplaceItemsContext from '@/contexts/MarketplaceItemsContext';
import CloseIcon from '@/icons/close-icon.svg';
import MagnifierIcon from '@/icons/magnifier-icon.svg';

import styles from './MasterSearchBar.module.scss';
import { useCurrentPageCache } from '@/hooks/useCurrentPageCache';

type Props = {
  isShowingNFTs: boolean;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
};

const MasterSearchBar: VFC<Props> = ({
  isShowingNFTs,
  searchTerm,
  setSearchTerm,
}: Props) => {
  const { t } = useTranslation();
  const [searchBarIsOnFocus, setSearchBarIsOnFocus] = useState(false);
  const [inSearchModal, setInSearchModal] = useState(false);

  const { getCacheContent: getCachedSearchResult } = useCurrentPageCache();

  const [dropdownSearchResults, setDropdownSearchResults] = useState({
    users: [],
    creations: [],
  });
  const {
    searchConditionNFTs,
    searchConditionUsers,
    setSearchConditionNFTs,
    setSearchConditionUsers,
  } = useContext(MarketplaceItemsContext);

  const searchAndUpdateDropdown = async () => {
    if (searchTerm.length) {
      setDropdownSearchResults(
        await getCachedSearchResult(searchTerm, () => masterSearch(searchTerm))
      );
    }
  };

  const searchAndUpdateMarketplace = async (
    customKeyword: null | string = null
  ) => {
    const hasCustomKeyword = customKeyword !== null;
    if (isShowingNFTs) {
      const newSearchConditionNFTs = {
        ...searchConditionNFTs,
        keyword: hasCustomKeyword ? customKeyword : searchTerm,
      };
      setSearchConditionNFTs(newSearchConditionNFTs);
    } else {
      const newSearchConditionUsers = {
        ...searchConditionUsers,
        keyword: hasCustomKeyword ? customKeyword : searchTerm,
      };
      setSearchConditionUsers(newSearchConditionUsers);
    }
  };

  // Four ways to trigger search-related updates:
  // 1. searchTerm change triggers deplayed changes in dropdown
  useEffect(() => {
    // if dependency changes multiple times in the 1 sec, useEffect only runs the last dependency
    const delaySearch = setTimeout(async () => {
      await searchAndUpdateDropdown();
    }, 1000);
    return () => clearTimeout(delaySearch);
  }, [searchTerm]);

  // 2. Pressing Enter button triggers immediate changes in both dropdown and marketplace
  const keyPress = async (e) => {
    if (e.key === 'Enter') {
      await searchAndUpdateDropdown(); // do it now instead of waiting delaySearch
      await searchAndUpdateMarketplace();
    }
  };

  // 3. Clicking close button triggers delayed change in dropdown and immediate change in marketplace
  const handleClickCloseButton = async () => {
    setSearchTerm('');
    await searchAndUpdateMarketplace('');
  };

  return (
    <div className={styles.masterSearchBarWrapper}>
      <input
        className={styles.masterSearchBar}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={keyPress}
        placeholder={t('filterSidebar.searchbar.placeholder')}
        onFocus={() => setSearchBarIsOnFocus(true)}
        onBlur={() => setSearchBarIsOnFocus(false)}
        value={searchTerm}
      />
      <MagnifierIcon
        className={styles.magnifierIcon}
        // 4. Clicking search button triggers immediate changes in both dropdown and marketplace
        onClick={async () => {
          await searchAndUpdateDropdown();
          await searchAndUpdateMarketplace();
        }}
      />
      {searchTerm !== '' && (
        <CloseIcon
          className={styles.closeIcon}
          onClick={handleClickCloseButton}
        />
      )}
      {(searchBarIsOnFocus || inSearchModal) && (
        <MasterSearchModal
          searchResults={dropdownSearchResults}
          onMouseLeave={() => setInSearchModal(false)}
          onMouseEnter={() => setInSearchModal(true)}
        />
      )}
    </div>
  );
};

export default MasterSearchBar;
